import type { Platform } from '../../../shared/CharacterTypes';
import type { IGraphics } from '../../rendering/IRenderer';

type SurfaceKind = 'tomb' | 'square' | 'earth' | 'cedar' | 'slate' | 'stone-step' | 'timber-step';
type Inlay = 'none' | 'bronze' | 'armory' | 'apothecary';
export interface OpeningAreaMaterial {
  readonly kind: SurfaceKind;
  readonly face: number;
  readonly deep: number;
  readonly mortar: number;
  readonly cap: number;
  readonly rim: number;
  readonly stones: readonly number[];
  readonly inlay: Inlay;
  readonly trim: number;
}
type OpeningGraphics = Pick<IGraphics, 'fillRect' | 'fillPolygon' | 'fillVerticalGradientRect'>;
type Bounds = Pick<Platform, 'x' | 'y' | 'width' | 'height'>;

const TOMB: OpeningAreaMaterial = {
  kind: 'tomb', face: 0x443c48, deep: 0x1b1d2a, mortar: 0x252532,
  cap: 0x817063, rim: 0xb5a08a, stones: [0x514650, 0x49434e, 0x62525b, 0x484654],
  inlay: 'none', trim: 0xa18a62,
};
const VILLAGE: OpeningAreaMaterial = {
  kind: 'square', face: 0x60554f, deep: 0x272b3c, mortar: 0x393943,
  cap: 0xa48e74, rim: 0xd1ba92, stones: [0x79716e, 0x696b79, 0x8a776b, 0x727080],
  inlay: 'none', trim: 0xa78d68,
};
const EARTH: OpeningAreaMaterial = { ...VILLAGE, kind: 'earth', face: 0x655144, deep: 0x292a38,
  cap: 0x958069, stones: [0x786754, 0x807264, 0x676473, 0x8c765c] };
const CEDAR: OpeningAreaMaterial = { ...VILLAGE, kind: 'cedar', face: 0x765448, deep: 0x302737,
  cap: 0xaf8562, rim: 0xd5ad7b, stones: [0x8a604e, 0x765245, 0x996e54, 0x805449] };
const SLATE: OpeningAreaMaterial = { ...VILLAGE, kind: 'slate', face: 0x555969, deep: 0x242938,
  cap: 0x85879a, rim: 0xbab1ac, stones: [0x64677c, 0x535c70, 0x737285, 0x606376] };
const STONE_STEP: OpeningAreaMaterial = { ...VILLAGE, kind: 'stone-step' };
const TIMBER_STEP: OpeningAreaMaterial = { ...CEDAR, kind: 'timber-step', cap: 0xa88664 };
const ARMORY: OpeningAreaMaterial = { ...TOMB, inlay: 'armory', trim: 0x8799a7 };
const APOTHECARY: OpeningAreaMaterial = { ...TOMB, inlay: 'apothecary', trim: 0x7b9b8d };
const RELIC: OpeningAreaMaterial = { ...TOMB, inlay: 'bronze', trim: 0xb99a65 };

function validBounds(p: Bounds): boolean {
  return [p.x, p.y, p.width, p.height, p.x + p.width, p.y + p.height].every(Number.isFinite)
    && p.width > 0 && p.height > 0 && p.x + p.width > p.x && p.y + p.height > p.y;
}

/** Exact map/style admission; shared town and port styles never opt in by accident. */
export function openingMaterialFor(levelId: string, p: Platform): OpeningAreaMaterial | undefined {
  if (p.type !== 'normal' || p.solid === false || p.id.startsWith('wall') || !validBounds(p)) return undefined;
  const style = p.properties?.visualStyle;
  if (levelId === 'tomb_waking' && (style === 'tomb_floor' || style === 'tomb_landing')) {
    if (p.id === 'waking_armory_shelf') return ARMORY;
    if (p.id === 'waking_apothecary_shelf') return APOTHECARY;
    if (p.id === 'waking_slab' || p.id === 'relic_shelf') return RELIC;
    return TOMB;
  }
  if (levelId !== 'village_arrival') return undefined;
  if (style === 'village_ground') return p.id === 'village_floor_square' ? VILLAGE : EARTH;
  if (style !== 'village_ledge') return undefined;
  if (p.id.startsWith('cottage_roof')) return CEDAR;
  if (p.id === 'well_roof') return SLATE;
  return p.id === 'hearth_ledge_2' ? TIMBER_STEP : STONE_STEP;
}

function seedFor(id: string): number {
  let seed = 2166136261;
  for (let i = 0; i < id.length; i++) seed = Math.imul(seed ^ id.charCodeAt(i), 16777619);
  return seed >>> 0;
}
function variation(seed: number, index: number): number {
  const value = Math.imul(seed ^ Math.imul(index + 1, 374761393), 668265263);
  return ((value ^ (value >>> 13)) >>> 0) / 4294967296;
}

/** All primitives use local coordinates and are clipped inside the authored body. */
function painter(g: OpeningGraphics, p: Bounds) {
  const cx = (value: number) => Math.max(0, Math.min(p.width, value));
  const cy = (value: number) => Math.max(0, Math.min(p.height, value));
  return {
    rect(x: number, y: number, width: number, height: number, color: number, alpha = 1) {
      const left = cx(x), top = cy(y), right = cx(x + width), bottom = cy(y + height);
      if (right > left && bottom > top) g.fillRect(p.x + left, p.y + top, right - left, bottom - top, color, alpha);
    },
    polygon(points: ReadonlyArray<readonly [number, number]>, color: number, alpha = 1) {
      g.fillPolygon(points.map(([x, y]) => ({ x: p.x + cx(x), y: p.y + cy(y) })), color, alpha);
    },
  };
}
type Paint = ReturnType<typeof painter>;
const MAX_COLUMNS = 18;
const MAX_COURSES = 4;

function courses(draw: Paint, p: Bounds, m: OpeningAreaMaterial, seed: number, cap: number,
  depth: number, nominalWidth: number, nominalHeight: number): void {
  const columns = Math.min(MAX_COLUMNS, Math.max(1, Math.ceil(p.width / nominalWidth)));
  const rows = Math.min(MAX_COURSES, Math.max(1, Math.ceil(depth / nominalHeight)));
  const step = p.width / columns, course = depth / rows;
  for (let row = 0; row < rows; row++) {
    const top = cap + row * course, bottom = Math.min(p.height, top + course);
    for (let col = 0; col <= columns; col++) {
      const left = Math.max(0, (col - (row % 2) * .5) * step);
      const right = Math.min(p.width, (col + 1 - (row % 2) * .5) * step);
      if (right - left < 5 || bottom - top < 5) continue;
      const v = variation(seed, row * 31 + col), inset = Math.min(4, (right - left) * .06, course * .15);
      const color = m.stones[Math.floor(v * m.stones.length)];
      draw.polygon([[left + 1 + inset, top + 1], [right - 1, top + 1],
        [right - 1, bottom - 1 - inset], [right - 1 - inset, bottom - 1],
        [left + 1, bottom - 1], [left + 1, top + 1 + inset]], color, .9 - row * .13);
      draw.rect(left + 2 + inset, top + 2, right - left - inset - 5, 1, m.cap, .42 - row * .07);
      if (v > .7 && right - left > 30 && course > 15) {
        const cutX = left + (right - left) * .63;
        draw.polygon([[cutX, top + 2], [cutX + 3, top + course * .4],
          [cutX + 1, top + course * .67], [cutX + 1, top + course * .4]], m.mortar, .55);
      }
    }
  }
}

function capAndWear(draw: Paint, p: Bounds, m: OpeningAreaMaterial, seed: number, cap: number): void {
  draw.rect(0, 0, p.width, cap, m.cap);
  const count = Math.min(12, Math.max(1, Math.ceil(p.width / 120))), step = p.width / count;
  if (cap > 4) for (let i = 0; i < count; i++) {
    const left = step * (i + .24), width = step * (.17 + variation(seed, i + 500) * .18);
    draw.polygon([[left, cap * .65], [left + width, cap * .42],
      [left + width * .8, cap], [left + width * .14, cap]], m.face, .58);
    if (i > 0) draw.rect(i * step, 2, 1, cap - 2, m.mortar, .65);
  }
  // The full-width top remains exactly the collision plane, including worn caps.
  draw.rect(0, 0, p.width, Math.min(2, p.height), m.rim, .95);
}

function inlay(draw: Paint, p: Bounds, m: OpeningAreaMaterial, cap: number): void {
  if (m.inlay === 'none' || p.width < 40 || p.height < 24) return;
  const inset = Math.min(18, p.width * .12), y = cap + (p.height - cap) * .38;
  const count = Math.min(6, Math.max(1, Math.floor(p.width / 180)));
  const radius = Math.min(4, (p.height - cap) * .12);
  draw.rect(inset, y, p.width - inset * 2, 1, m.trim, .55);
  for (let i = 0; i < count; i++) {
    const x = p.width * (i + .5) / count;
    if (m.inlay === 'armory') {
      draw.rect(x - 3, cap + 2, 6, Math.max(1, p.height - cap - 7), m.deep, .8);
      draw.rect(x - 2, cap + 3, 4, 3, m.trim, .85);
      draw.rect(x - 2, p.height - 8, 4, 2, m.trim, .7);
    } else {
      draw.polygon([[x, y - radius], [x + radius, y], [x, y + radius], [x - radius, y]], m.trim, .85);
    }
  }
}

function earthFace(draw: Paint, p: Bounds, m: OpeningAreaMaterial, seed: number, cap: number): void {
  courses(draw, p, m, seed, cap, Math.min(28, p.height - cap), 100, 28);
  const count = Math.min(12, Math.max(1, Math.ceil(p.width / 100))), step = p.width / count;
  for (let i = 0; i < count; i++) {
    const top = cap + (p.height - cap) * (.3 + variation(seed, i) * .25);
    draw.polygon([[step * (i + .08), top], [step * (i + .78), top - 4],
      [step * (i + .63), top + 5], [step * (i + .2), top + 9]], m.deep, .3);
  }
}

/** Owns the complete rectangular body. No supports, overhangs, animation or extra Graphics. */
export function drawOpeningAreaMaterial(g: OpeningGraphics, p: Platform, m: OpeningAreaMaterial): void {
  if (!validBounds(p)) return;
  const draw = painter(g, p), seed = seedFor(p.id);
  if (g.fillVerticalGradientRect) g.fillVerticalGradientRect(p.x, p.y, p.width, p.height, m.face, m.deep, 1);
  else g.fillRect(p.x, p.y, p.width, p.height, m.face, 1);
  const roof = m.kind === 'cedar' || m.kind === 'slate';
  const cap = Math.min(p.height * .22, roof ? 5 : 14);
  if (m.kind === 'earth') earthFace(draw, p, m, seed, cap);
  else if (m.kind === 'timber-step') {
    const count = Math.min(12, Math.max(1, Math.ceil(p.width / 45))), step = p.width / count;
    for (let i = 0; i < count; i++) {
      draw.rect(step * i + step * .15, cap + (p.height - cap) * .4, step * .67, 1, m.cap, .65);
      if (i > 0) draw.rect(step * i, cap, 1, p.height - cap, m.deep, .75);
    }
    draw.rect(0, p.height - Math.min(4, p.height), p.width, Math.min(4, p.height), m.deep);
  } else {
    const depth = Math.min(p.height - cap, roof ? 28 : m.kind === 'square' ? 90 : 180);
    courses(draw, p, m, seed, cap, depth, roof ? 28 : m.kind === 'square' ? 58 : 104, roof ? 12 : 42);
    if (roof) draw.rect(0, p.height - Math.min(4, p.height), p.width, Math.min(4, p.height), m.deep);
  }
  capAndWear(draw, p, m, seed, cap);
  inlay(draw, p, m, cap);
}
